// Pure coordinate conversion between board indices (Pt, y=0 = TOP row) and
// GTP vertices ('Q16'). This is the ONLY place coordinate math lives.
//
// GTP columns run A..T skipping 'I'; GTP row 1 is the BOTTOM row.
// So on 19x19, vertex 'A19' = Pt 0 (top-left) and 'A1' = Pt 342 (bottom-left).

import type { Pt, Vertex } from '../types.ts';

/** GTP column letters in order, skipping 'I'. Index = x coordinate. */
export const GTP_COLUMNS = 'ABCDEFGHJKLMNOPQRST';

export function isPass(vertex: Vertex): boolean {
  return vertex.toLowerCase() === 'pass';
}

export function isResign(vertex: Vertex): boolean {
  return vertex.toLowerCase() === 'resign';
}

/** True for vertices that name a board point (not 'pass'/'resign'). */
export function isBoardVertex(vertex: Vertex): boolean {
  return !isPass(vertex) && !isResign(vertex);
}

/** Convert a board index to a GTP vertex like 'Q16'. Throws if out of range. */
export function ptToVertex(pt: Pt, size: number): Vertex {
  if (!Number.isInteger(pt) || pt < 0 || pt >= size * size) {
    throw new Error(`ptToVertex: pt ${pt} out of range for size ${size}`);
  }
  const x = pt % size;
  const y = (pt - x) / size;
  return `${GTP_COLUMNS.charAt(x)}${size - y}`;
}

/** Convert a GTP vertex like 'Q16' to a board index. Throws on 'pass'/'resign' or out-of-range. */
export function vertexToPt(vertex: Vertex, size: number): Pt {
  if (!isBoardVertex(vertex)) {
    throw new Error(`vertexToPt: '${vertex}' is not a board vertex`);
  }
  const normalized = vertex.toUpperCase();
  const match = /^([A-HJ-T])(\d{1,2})$/.exec(normalized);
  if (!match) throw new Error(`vertexToPt: malformed vertex '${vertex}'`);
  const x = GTP_COLUMNS.indexOf(match[1]!);
  const row = Number.parseInt(match[2]!, 10);
  if (x < 0 || x >= size || row < 1 || row > size) {
    throw new Error(`vertexToPt: '${vertex}' out of range for size ${size}`);
  }
  const y = size - row;
  return y * size + x;
}
